import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { usePrevious } from '@hivebrite/react-hooks';

import { useCommentsClient } from 'hb-react/frontoffice/apps/CommentsV2/hooks/commentsClient';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';

import { LoadMore } from './LoadMore';
import { SimpleComment } from './SimpleComment';
import { orderCommentsByAsc } from '../utils';

const Replies = ({
  canDisplayEmojiPicker,
  commentableId,
  commentableType,
  currentUser,
  isOpenInModal,
  onDeleteReply,
  onReply,
  onToggleLike,
  onUpdateReplies,
  onUpdateReply,
  replies: initialReplies,
}) => {
  const queryClient = useQueryClient();

  const [replies, setReplies] = useState([]);
  const [totalReplies, setTotalReplies] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedReplies, setDisplayedReplies] = useState([]);

  const prevInitialReplies = usePrevious(initialReplies);

  const { getComments } = useCommentsClient();

  const infiniteQueryKey = useMemo(
    () => [
      'paginated-replies',
      commentableType,
      commentableId,
      isOpenInModal ? 'modal' : 'page',
    ],
    [commentableType, commentableId, isOpenInModal],
  );

  const { isFetching, hasNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: infiniteQueryKey,
    queryFn: async ({ pageParam }) => {
      try {
        return await getComments({
          commentableId,
          commentableType,
          page: pageParam,
          perPage: 5,
        });
      } catch (error) {
        if (error?.response?.status === 403) {
          displayXhrError(error);
        }
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: ({ _metadata: { page, has_next_page: hasNext } }) =>
      hasNext ? page + 1 : undefined,
  });

  const loadMoreReplies = useCallback(() => {
    if (fetchNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage]);

  useEffect(() => {
    if (
      !isEqual(prevInitialReplies, initialReplies) &&
      initialReplies.items &&
      initialReplies.items.length > 0
    ) {
      setReplies(initialReplies.items);
      setTotalReplies(initialReplies.total);
    }
  }, [prevInitialReplies, initialReplies]);

  useEffect(() => {
    if (!isFetching && !isEmpty(data)) {
      const { pages = [] } = data;

      const loadedCurrentPage = pages.length;

      if (loadedCurrentPage !== currentPage && pages[currentPage]) {
        const { replies: loadedReplies, _metadata } = pages[currentPage];

        if (loadedReplies.length > 0) {
          const newItems = loadedReplies.filter(
            (item) =>
              replies.find((itemOriginal) => itemOriginal.id === item.id) ===
              undefined,
          );

          const newReplies = [...replies, ...newItems];

          setReplies(newReplies);
          setTotalReplies(_metadata.total_items);
          setCurrentPage(currentPage + 1);

          onUpdateReplies({
            replies: newReplies,
            totalReplies: _metadata.total_items,
          });
        }
      }
    }
  }, [isFetching, data, replies, currentPage]);

  useEffect(() => {
    if (totalReplies > 0) {
      setDisplayedReplies(orderCommentsByAsc(replies));
    }
  }, [replies, totalReplies]);

  useEffect(
    () => () => {
      queryClient.setQueryData(infiniteQueryKey, () => ({
        pageParams: [],
        pages: [],
      }));
    },
    [],
  );

  if (isFetching && totalReplies === 0) {
    return (
      <Box py={6}>
        <Loader size={15} />
      </Box>
    );
  }

  return (
    <Box height="100%" width="100%" pl={6} flexDirection="column">
      <Box alignSelf="flex-start" mb={3}>
        {hasNextPage && (
          <LoadMore
            isFetching={isFetching}
            loadMore={loadMoreReplies}
            loadingText={s__('Replies|Loading...')}
            linkText={s__('Replies|See more replies')}
          />
        )}
      </Box>

      {totalReplies > 0 && (
        <RepliesWrapper>
          {displayedReplies.map((reply, index) => (
            <Box
              pb={index === displayedReplies.length - 1 ? 0 : 3}
              data-testid="comment-reply"
              key={reply.id}
            >
              <SimpleComment
                canDisplayEmojiPicker={canDisplayEmojiPicker}
                comment={reply}
                currentUser={currentUser}
                isReply
                onDeleteComment={onDeleteReply}
                onReply={onReply}
                onToggleLike={onToggleLike}
                onUpdateComment={onUpdateReply}
              />
            </Box>
          ))}
        </RepliesWrapper>
      )}
    </Box>
  );
};

export { Replies };

const RepliesWrapper = styled(Box)`
  flex-direction: column;
  width: 100%;
  position: relative;
  margin-bottom: 8px;

  &:before {
    content: '';
    position: absolute;
    left: -24px;
    width: 1px;
    height: 100%;
    background: #cecece;
    border-radius: 1px;
  }
`;
